import { useEffect, useState } from "react";
import { Inbox, UserCheck, UserX, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "../api";

const ROLE_LABELS = { admin: "Clinic Admin", clinic_manager: "Clinic Manager", front_desk: "Front Desk", assistant: "Assistant" };
const STATUS_STYLE = {
  pending: "bg-gold-soft/70 text-pine-deep",
  approved: "bg-teal-soft text-teal-brand",
  rejected: "bg-red-50 text-red-500",
};

export default function Requests() {
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState({});
  const [creds, setCreds] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/connect/access-requests").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/connect/access-requests/${id}/approve`, { role: roles[id] || "clinic_manager" });
      setCreds(data);
      toast.success(`Approved — account created for ${data.email}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not approve");
    }
  };

  const reject = async (id) => {
    try {
      await api.post(`/connect/access-requests/${id}/reject`);
      toast.success("Request rejected");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reject");
    }
  };

  const filtered = items.filter((r) => filter === "all" || r.status === filter);
  const pendingCount = items.filter((r) => r.status === "pending").length;

  return (
    <div data-testid="connect-requests" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-pine-deep">Access Requests</h2>
          <p className="text-sm text-slate-500">{pendingCount} pending · approve to create accounts with the role you assign.</p>
        </div>
        <div className="flex gap-2">
          {["all", "pending", "approved", "rejected"].map((f) => (
            <button key={f} data-testid={`requests-filter-${f}`} onClick={() => setFilter(f)}
              className={`rounded-full px-4 py-2 text-xs font-bold border capitalize transition-all ${filter === f ? "bg-pine text-ivory border-pine" : "bg-white text-pine border-pine/15"}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {creds && (
        <div data-testid="requests-approved-creds" className="rounded-2xl bg-teal-soft border border-teal-brand/30 p-5 flex flex-wrap items-center gap-4">
          <CheckCircle2 className="h-6 w-6 text-teal-brand shrink-0" />
          <div className="text-sm flex-1">
            <p className="font-bold text-pine-deep">
              {creds.credentials_emailed
                ? `Account created — login details emailed to ${creds.email}.`
                : "Account created — share these credentials manually:"}
            </p>
            <p className="text-slate-600">Email: <span className="font-bold">{creds.email}</span> · Role: {creds.role}</p>
            <p className="text-xs text-slate-500 mt-1">Backup temp password (share only if the email didn't arrive): <span className="font-bold">{creds.password}</span></p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { navigator.clipboard?.writeText(`Email: ${creds.email} | Password: ${creds.password}`); toast.success("Copied"); }} className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-bold text-pine border border-pine/15">
              <Copy className="h-3.5 w-3.5" /> Copy
            </button>
            <button onClick={() => setCreds(null)} className="text-xs font-bold text-slate-500 hover:text-pine">Dismiss</button>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-pine/8 shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b border-pine/8 text-left text-[11px] uppercase tracking-wider text-slate-400">
                <th className="px-5 py-3.5 font-bold">Name</th>
                <th className="px-5 py-3.5 font-bold">Contact</th>
                <th className="px-5 py-3.5 font-bold">Clinic</th>
                <th className="px-5 py-3.5 font-bold">Asked For</th>
                <th className="px-5 py-3.5 font-bold">Partnership</th>
                <th className="px-5 py-3.5 font-bold">Date</th>
                <th className="px-5 py-3.5 font-bold">Status</th>
                <th className="px-5 py-3.5 font-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`request-row-${r.id.slice(0, 8)}`} className="border-b border-pine/5 last:border-0 hover:bg-cream/50">
                  <td className="px-5 py-3.5 font-bold text-pine-deep">{r.name}</td>
                  <td className="px-5 py-3.5 text-slate-600">
                    <p>{r.email}</p><p className="text-xs text-slate-400">{r.phone}</p>
                  </td>
                  <td className="px-5 py-3.5 text-slate-600 text-xs">{r.clinic}</td>
                  <td className="px-5 py-3.5 text-slate-600">{r.role}</td>
                  <td className="px-5 py-3.5 text-xs text-slate-500">{r.partnership || "Dyntra Clinic Partner"}</td>
                  <td className="px-5 py-3.5 text-xs text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "-"}</td>
                  <td className="px-5 py-3.5">
                    <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase ${STATUS_STYLE[r.status] || STATUS_STYLE.pending}`}>
                      {r.status}{r.assigned_role ? ` · ${ROLE_LABELS[r.assigned_role] || r.assigned_role}` : ""}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    {r.status === "pending" && (
                      <div className="flex items-center gap-2">
                        <select
                          data-testid={`request-role-${r.id.slice(0, 8)}`}
                          value={roles[r.id] || "clinic_manager"}
                          onChange={(e) => setRoles({ ...roles, [r.id]: e.target.value })}
                          className="h-9 rounded-xl border border-pine/15 px-2.5 text-xs font-bold bg-white focus:outline-none focus:ring-2 focus:ring-pine"
                        >
                          {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        <button data-testid={`request-approve-${r.id.slice(0, 8)}`} onClick={() => approve(r.id)} className="inline-flex items-center gap-1 rounded-full bg-pine px-3.5 py-2 text-[11px] font-bold text-ivory hover:bg-pine-deep">
                          <UserCheck className="h-3.5 w-3.5" /> Approve
                        </button>
                        <button data-testid={`request-reject-${r.id.slice(0, 8)}`} onClick={() => reject(r.id)} className="inline-flex items-center gap-1 rounded-full border border-red-200 px-3.5 py-2 text-[11px] font-bold text-red-500 hover:bg-red-50">
                          <UserX className="h-3.5 w-3.5" /> Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400">
                  <Inbox className="h-8 w-8 mx-auto mb-2 text-slate-300" /> No access requests here yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
